import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Language = () => {
  const [activeMenu, setActiveMenu] = useState('language');
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [translationsPercentage, setTranslationsPercentage] = useState(50);
  const [isLanguageDisabled, setIsLanguageDisabled] = useState(false);
  const [isCurrencyLocalized, setIsCurrencyLocalized] = useState(false);

  // Handle language change
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
  };

  // Handle translation range change
  const handleRangeChange = (e) => {
    setTranslationsPercentage(e.target.value);
  };

  // Toggle disable language checkbox
  const handleLanguageDisableToggle = () => {
    setIsLanguageDisabled((prev) => !prev);
  };

  // Toggle currency localization checkbox
  const handleCurrencyLocalizationToggle = () => {
    setIsCurrencyLocalized((prev) => !prev);
  };

  return (
    <div className="flex bg-gray-100">
      {/* Sidebar */}
      <div className="w-1/5 bg-gray-100 border-r text-black hover:text-gray-800 p-4">
        <h1 className="text-xl font-bold mb-4">Settings</h1>
        <div>
          {/* Sidebar Links */}
          <Link
            to="/dashboard/system-config/wide-settings"
            className="w-full block text-left py-2 px-4 mb-2 rounded hover:bg-gray-200 "
          >
            General
          </Link>
          <Link
            to="/dashboard/system-config/preferences"
            className="w-full block text-left py-2 px-4 mb-2 rounded hover:bg-gray-200"
          >
            Preferences
          </Link>

          <Link
            to="/dashboard/system-config/notifications"
            className="w-full block text-left py-2 px-4 mb-2 rounded hover:bg-gray-200"
          >
            Notifications
          </Link>

          <Link
            to="/dashboard/system-config/language"
            className="w-full block text-left py-2 px-4 mb-2 rounded bg-black text-white "
          >
            Language Settings
          </Link>

          <Link
            to="/dashboard/system-config/currency"
            className="w-full block text-left py-2 px-4 mb-2 rounded hover:bg-gray-200"
          >
            Currency Settings
          </Link>

          <Link
            to="/dashboard/system-config/hotel-config"
            className="w-full block text-left py-2 px-4 mb-2 rounded hover:bg-gray-200"
          >
            Hotel Configuration
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-5/6 bg-gray-50 p-8 flex flex-col justify-start">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl text-black font-bold">Settings &gt; Language</h2>
              <p className="text-gray-500 font-semibold">
                Manage the languages available on the website for your region
              </p>
            </div>
            <div className="flex space-x-4">
              <button className="px-6 py-2 bg-cyan-500 text-white text-sm font-semibold rounded shadow-md hover:text-black transition-all">
                Save
              </button>
              <button className="px-6 py-2 bg-red-500 text-white text-sm font-semibold rounded shadow-md hover:text-black transition-all">
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Section */}
        <div className="bg-white p-6 flex-grow">
          {/* Language Header */}
          <div className="mb-4">
            <h1 className="text-xl font-bold text-black">Language</h1>
            <p className="text-gray-500 font-semibold">
              Manage the languages available on the website for your region
            </p>
            <hr className="border-t border-gray-300 mt-1" />
          </div>

          {/* Select Language */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold">Select Your Language</h2>
            <div className="flex items-center mt-4 space-x-4">
              <button
                className={`border px-4 py-2 rounded-md flex items-center space-x-2 ${
                  selectedLanguage === 'English' ? 'bg-gray-300' : ''
                }`}
                onClick={() => handleLanguageChange('English')}
              >
                <span className="font-semibold">🇬🇧 English</span>
              </button>
              <button
                className={`border px-4 py-2 rounded-md flex items-center space-x-2 ${
                  selectedLanguage === 'Hindi' ? 'bg-gray-300' : ''
                }`}
                onClick={() => handleLanguageChange('Hindi')}
              >
                <span>🇮🇳 Hindi</span>
              </button>
              <button
                className={`border px-4 py-2 rounded-md flex items-center space-x-2 ${
                  selectedLanguage === 'Spanish' ? 'bg-gray-300' : ''
                }`}
                onClick={() => handleLanguageChange('Spanish')}
              >
                <span>🇪🇸 Spanish</span>
              </button>
              <button
                className={`border px-4 py-2 rounded-md flex items-center space-x-2 ${
                  selectedLanguage === 'French' ? 'bg-gray-300' : ''
                }`}
                onClick={() => handleLanguageChange('French')}
              >
                <span>🇫🇷 French</span>
              </button>
            </div>
          </div>

          {/* Default Language */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Default Language</h2>
                <p className="text-gray-500 font-semibold">
                  Indicate which language is set as the website's default
                </p>
              </div>
              <select
                className="border p-2 rounded-md w-1/4"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                <option>English</option>
                <option>Hindi</option>
                <option>Spanish</option>
                <option>French</option>
              </select>
            </div>
          </div>

          {/* Translations */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Translations</h2>
                <p className="text-gray-500 font-semibold">
                  Whether the language is fully translated or partially done
                </p>
              </div>
              <div className="flex items-center gap-4 w-1/4">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={translationsPercentage}
                  onChange={handleRangeChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-gray-700 font-semibold">{translationsPercentage}%</span>
              </div>
            </div>
          </div>
          <hr className="border-t border-gray-300 mt-1 mb-6" />

          {/* Disable Language */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Disable Language</h2>
                <p className="text-gray-500 font-semibold">
                  Allow the admin to enable or disable specific languages on the website.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isLanguageDisabled}
                  onChange={handleLanguageDisableToggle}
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-600 rounded-full peer-checked:bg-blue-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                <div className=""></div>
              </label>
            </div>
          </div>

          {/* Currency Localization */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Currency Localization</h2>
                <p className="text-gray-500 font-semibold">
                  Adjust currency format based on the language (e.g., USD for English)
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isCurrencyLocalized}
                  onChange={handleCurrencyLocalizationToggle}
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-600 rounded-full peer-checked:bg-blue-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                <div className=""></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Language;
